import AddIcon from '@mui/icons-material/Add'
import AttachmentIcon from '@mui/icons-material/Attachment'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import {
    Autocomplete,
    Badge,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import FormStepper from '../../../components/form-stepper'
import GrantContent from '../../../components/GrantContent'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon as CheckMarkIcon } from '../../../template/kartera/icons'
import { GENEROS, PARENTESCOS } from '../../../utils/catalogs'
import * as Permisos from '../../../utils/Permisos'
import CustomerForm from '../../customers/form/customer-form'
import PolicyDrawer from '../drawer/policy-drawer'
import { personName, RamoMark, selectMenu } from '../common/policy-common'
import {
    ACCENT,
    aseguradoraLabel,
    claveLabel,
    fieldHelp,
    fmtShort,
    FORMA_PAGO,
    formatAmount,
    formatPct,
    edadText,
    LAST_STEP,
    monedaLabel,
    MODELO_YEARS,
    nacimientoText,
    NO_SERIE_MAX,
    parentescoLabel,
    RAMO_META,
    RAMO_OPTIONS,
    RECIBO_FIELDS,
    usePolicyFormModel,
    vehiculoLabel,
} from './policy-form-model'
import type { AseguradoForm, RamoKey, VehiculoOption } from './policy-form-model'

const outlineBtnSx = {
    width: 'auto',
    minWidth: 'auto',
    flex: 'none',
    height: 40,
    padding: '0 16px',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    backgroundColor: 'rgb(var(--k-surface))',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line))',
    '&:hover': {
        backgroundColor: 'rgb(var(--k-hover))',
        border: '1px solid rgb(var(--k-line))',
        color: 'rgb(var(--k-ink))',
        boxShadow: 'none',
    },
} as const
const primaryBtnSx = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-primary))',
    color: '#fff',
    border: 0,
    fontWeight: 700,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-primary))',
        filter: 'brightness(1.05)',
        boxShadow: 'none',
        color: '#fff',
    },
} as const
const dangerBtnSx = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-danger))',
    color: '#fff',
    border: 0,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-danger))',
        filter: 'brightness(1.08)',
        boxShadow: 'none',
        color: '#fff',
    },
} as const
const newClientBtnSx: SxProps<Theme> = {
    ...outlineBtnSx,
    height: 52,
    width: 52,
    minWidth: 52,
    padding: 0,
    // Alinea con el input filled: label estático (15px × 1.25) + margin 8px
    marginTop: 'calc(15px * 1.25 + 8px)',
    borderRadius: '12px',
    border: 0,
    backgroundColor: 'rgb(var(--k-accent-soft))',
    color: 'rgb(var(--k-accent-ink))',
    '& .MuiSvgIcon-root': { color: 'inherit' },
    '&:hover': {
        backgroundColor: 'rgb(var(--k-accent-soft))',
        color: 'rgb(var(--k-accent-ink))',
        filter: 'brightness(0.97)',
        boxShadow: 'none',
    },
}
const checkboxSx = { color: 'rgb(var(--k-muted))', '&.Mui-checked': { color: 'rgb(var(--k-primary))' } }
const totalRowBorder = { borderTop: '2px solid rgb(var(--k-ink))' }
/** El `standard` de MUI no hereda la paleta Kartera; sin esto el texto queda negro en tema oscuro. */
const reciboInputSx: SxProps<Theme> = {
    minWidth: 96,
    '& .MuiInputBase-input': {
        color: 'rgb(var(--k-ink))',
        WebkitTextFillColor: 'rgb(var(--k-ink))',
        caretColor: 'rgb(var(--k-ink))',
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
    },
    '& .MuiInput-underline:before': { borderBottomColor: 'rgb(var(--k-line-strong))' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgb(var(--k-muted))' },
    '& .MuiInput-underline:after': { borderBottomColor: 'rgb(var(--k-primary))' },
}
const amountInput = { startAdornment: <InputAdornment position="start">$</InputAdornment> }
const amountHtmlInput = { inputMode: 'decimal' as const, style: { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' } }
const amountCol = { textAlign: 'right' as const }

type PolicyFormModel = ReturnType<typeof usePolicyFormModel>

const aseguradoTitle = (ramo: RamoKey | '', asegurado: AseguradoForm, index: number) => {
    if (index === 0) return 'Titular'
    if (ramo === 'salud') return asegurado.parentesco ? parentescoLabel(asegurado.parentesco) : `Asegurado ${index + 1}`
    return `Asegurado ${index + 1}`
}

const AseguradosStep = ({ model }: { model: PolicyFormModel }) => {
    const { state, se, change } = model
    const salud = state.ramo === 'salud'
    return (
        <>
            <Dialog open={model.clienteIncompletoDialog} onClose={model.closeClienteIncompletoDialog} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Cliente incompleto</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">El cliente no cuenta con todos los datos para usarse como asegurado. Edita el cliente y completa los campos obligatorios.</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" sx={primaryBtnSx} onClick={model.closeClienteIncompletoDialog}>Aceptar</Button>
                </DialogActions>
            </Dialog>

            {state.asegurados.map((a, i) => {
                const locked = i === 0 && state.clienteEsAsegurado
                const err = (field: string) => se[`a${i}${field}`]
                return (
                    <div key={a.key} className="flex flex-col gap-4 rounded-[14px] border border-k-line bg-k-bg p-4">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-extrabold text-k-ink">{aseguradoTitle(state.ramo, a, i)}</span>
                            {i > 0 && (
                                <Button type="button" variant="text" sx={{ ...outlineBtnSx, border: 0, color: 'rgb(var(--k-danger))', height: 34 }} onClick={() => model.removeAsegurado(a.key)}>
                                    Quitar
                                </Button>
                            )}
                        </div>

                        {i === 0 && model.canUseContratante && (
                            <FormControlLabel
                                control={<Checkbox checked={state.clienteEsAsegurado} onChange={change.clienteEsAsegurado} sx={checkboxSx} />}
                                label="El contratante es también el asegurado"
                                className="!ml-0"
                            />
                        )}

                        <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                            {salud && (
                                <TextField
                                    className="v2-field v2-select md:col-span-2" variant="filled" fullWidth select required
                                    label="Parentesco"
                                    value={i === 0 ? 'titular' : a.parentesco}
                                    disabled={i === 0}
                                    error={!!err('parentesco')}
                                    helperText={i === 0 ? 'El primer asegurado siempre es el titular' : undefined}
                                    onChange={(e) => change.asegurado(a.key, 'parentesco', e.target.value)}
                                    slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                >
                                    <MenuItem value=""><em>Seleccione</em></MenuItem>
                                    {(i === 0 ? PARENTESCOS : model.parentescoOptions(a.key)).map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                                </TextField>
                            )}
                            <TextField
                                className="v2-field md:col-span-2" variant="filled" fullWidth required
                                label="Nombres"
                                value={a.nombres}
                                disabled={locked}
                                error={!!err('nombres')}
                                onChange={(e) => change.asegurado(a.key, 'nombres', e.target.value)}
                            />
                            <TextField
                                className="v2-field" variant="filled" fullWidth required
                                label="Apellido paterno"
                                value={a.apPat}
                                disabled={locked}
                                error={!!err('apPat')}
                                onChange={(e) => change.asegurado(a.key, 'apPat', e.target.value)}
                            />
                            <TextField
                                className="v2-field" variant="filled" fullWidth
                                label="Apellido materno"
                                value={a.apMat}
                                disabled={locked}
                                onChange={(e) => change.asegurado(a.key, 'apMat', e.target.value)}
                            />
                            {locked ? (
                                <TextField
                                    className="v2-field" variant="filled" fullWidth disabled
                                    label="Nacimiento"
                                    value={nacimientoText(a.fecNacimiento)}
                                    helperText="Tomada del contratante"
                                />
                            ) : (
                                <TextField
                                    className="v2-field" variant="filled" fullWidth
                                    label="Nacimiento"
                                    type="date"
                                    value={a.fecNacimiento}
                                    helperText={edadText(a.fecNacimiento)}
                                    onChange={(e) => change.asegurado(a.key, 'fecNacimiento', e.target.value)}
                                />
                            )}
                            <TextField
                                className="v2-field v2-select" variant="filled" fullWidth select required
                                label="Género"
                                value={a.genero}
                                disabled={locked && !!a.genero}
                                error={!!err('genero')}
                                onChange={(e) => change.asegurado(a.key, 'genero', e.target.value)}
                                slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                            >
                                <MenuItem value=""><em>Seleccione</em></MenuItem>
                                {GENEROS.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                            </TextField>
                            <TextField
                                className="v2-field md:col-span-2" variant="filled" fullWidth
                                label="RFC"
                                value={a.rfc}
                                disabled={locked}
                                helperText="Opcional · se calcula con el nombre y la fecha de nacimiento"
                                onChange={(e) => change.asegurado(a.key, 'rfc', e.target.value)}
                                slotProps={{ htmlInput: { style: { textTransform: 'uppercase' } } }}
                            />
                        </div>
                    </div>
                )
            })}

            <Button type="button" variant="outlined" startIcon={<AddIcon />} sx={outlineBtnSx} onClick={model.addAsegurado}>
                Agregar otro asegurado
            </Button>
        </>
    )
}

const VehiculoStep = ({ model }: { model: PolicyFormModel }) => {
    const { state, se, change } = model
    const v = state.vehiculo
    return (
        <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
            <TextField
                className="v2-field v2-select" variant="filled" fullWidth select required
                label="Modelo"
                value={v.modelo}
                error={!!se.vModelo}
                helperText="Año del vehículo; filtra la búsqueda de la descripción."
                onChange={(e) => change.vehiculoModelo(e.target.value)}
                slotProps={{ select: selectMenu }}
            >
                {MODELO_YEARS.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
            </TextField>
            <div className="hidden md:block" />
            <Autocomplete<VehiculoOption, false, false, true>
                className="md:col-span-2"
                freeSolo
                options={model.vehiculoChoices}
                loading={model.vehiculoSearching}
                value={v.descripcion}
                inputValue={model.vehiculoInput}
                filterOptions={(items) => items}
                loadingText="Buscando..."
                getOptionLabel={(option) => vehiculoLabel(option)}
                getOptionKey={(option) => (typeof option === 'string' ? option : `${option.libre ? 'libre' : option.amis}-${vehiculoLabel(option)}`)}
                isOptionEqualToValue={(a, b) => vehiculoLabel(a) === vehiculoLabel(b) && (typeof b === 'string' || a.amis === b.amis)}
                onChange={(_, value) => change.vehiculoDescripcion(value)}
                onInputChange={(_, value, reason) => change.vehiculoInput(value, reason)}
                onBlur={change.vehiculoBlur}
                renderOption={(props, option) => {
                    const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key }
                    return (
                        <li key={key} {...optionProps}>
                            {option.libre ? (
                                <span className="flex flex-col">
                                    <span className="font-semibold">Usar “{option.descripcion}”</span>
                                    <span className="text-xs text-k-muted">Captura libre, sin catálogo</span>
                                </span>
                            ) : (
                                <span className="flex items-center gap-3">
                                    {option.icon && <img src={option.icon} alt="" className="h-6 w-6 shrink-0 object-contain" />}
                                    <span className="flex flex-col">
                                        <span>{vehiculoLabel(option)}</span>
                                        {option.amis && <span className="text-xs text-k-muted">AMIS {option.amis}</span>}
                                    </span>
                                </span>
                            )}
                        </li>
                    )
                }}
                slotProps={{ paper: { className: 'v2-select-menu' } }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        className="v2-field"
                        variant="filled"
                        fullWidth
                        required
                        label="Descripción"
                        error={!!se.vDescripcion}
                        helperText="Escribe marca o versión (mín. 4 letras) y elige del catálogo, o usa el texto tal cual."
                    />
                )}
            />
            <TextField
                className="v2-field" variant="filled" fullWidth required
                label="No. de serie"
                value={v.noSerie}
                error={!!se.vNoSerie}
                helperText={`${v.noSerie.length}/${NO_SERIE_MAX} caracteres`}
                onChange={(e) => change.vehiculoNoSerie(e.target.value)}
                slotProps={{ htmlInput: { style: { textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums' } } }}
            />
            <TextField
                className="v2-field" variant="filled" fullWidth
                label="Placas"
                value={v.placas}
                helperText="Opcional"
                onChange={(e) => change.vehiculoPlacas(e.target.value)}
                slotProps={{ htmlInput: { style: { textTransform: 'uppercase' } } }}
            />
        </div>
    )
}

const BienAseguradoStep = ({ model }: { model: PolicyFormModel }) => (
    <TextField
        className="v2-field" variant="filled" fullWidth required multiline minRows={10}
        label="Detalle del bien asegurado"
        placeholder="Ubicación, características, coberturas y sumas aseguradas del bien…"
        value={model.state.texto}
        error={!!model.se.texto}
        helperText={fieldHelp(model.se, {}, 'texto')}
        onChange={(e) => model.change.texto(e.target.value)}
    />
)

const PolicyForm = () => {
    const model = usePolicyFormModel()
    const { state, catalogs, ramoMeta, se, change, steps } = model
    const g = state.generales
    const pageTitle = model.isEditing ? 'Editar póliza' : 'Captura de póliza'

    return (
        <KarteraAdminTemplate loading={false} title={pageTitle}>
            {model.isEditing && (
                <PolicyDrawer polizaId={model.adjuntosOpen ? state.polizaId : null} initialTab="adjuntos" onClose={model.closeAdjuntos} />
            )}

            <Dialog open={model.applyAllIndex != null} onClose={model.cancelApplyToAll} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Aplicar a recibos subsecuentes</DialogTitle>
                <DialogContent><p className="m-0 text-sm text-k-ink-2">Esta acción aplicará los mismos montos a todos los recibos subsecuentes.</p></DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelApplyToAll}>Cancelar</Button>
                    <Button variant="contained" sx={primaryBtnSx} onClick={model.applyToAll}>Aceptar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={model.claveDialog.show} onClose={model.closeClaveDialog} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Alerta</DialogTitle>
                <DialogContent><p className="m-0 text-sm text-k-ink-2">{model.claveDialog.message}</p></DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.closeClaveDialog}>Cancelar</Button>
                    <Button variant="contained" sx={primaryBtnSx} onClick={model.previewWithClave}>Aceptar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={model.resetDialog} onClose={model.closeResetDialog} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Volver a comenzar</DialogTitle>
                <DialogContent><p className="m-0 text-sm text-k-ink-2">Esta acción limpiará el formulario y perderás los datos capturados. ¿Deseas continuar?</p></DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.closeResetDialog}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.reiniciar}>Aceptar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={model.clientDialog} onClose={model.closeClientDialog} fullWidth maxWidth="md" slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>{model.clientDialogCliente ? 'Editar cliente' : 'Alta de cliente'}</DialogTitle>
                <DialogContent>
                    <CustomerForm modal cliente={model.clientDialogCliente} onCompleted={model.clientCreated} />
                </DialogContent>
            </Dialog>

            <div className="relative mx-auto flex w-full max-w-[1360px] flex-col gap-[18px]">
                {model.loading && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">Procesando…</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={pageTitle}
                    description={model.isEditing ? 'Actualiza los datos de la póliza.' : 'Versión ligera · captura por pasos con lectura de carátula.'}
                    backTo={model.backUrl}
                    backLabel="Pólizas"
                    actions={(
                        <div className="flex flex-wrap items-center gap-3">
                            {model.isEditing && (
                                <GrantContent permisos={[Permisos.ADJUNTOS_R]}>
                                    <Button type="button" variant="outlined" startIcon={<AttachmentIcon />} sx={outlineBtnSx} onClick={model.openAdjuntos}>
                                        Adjuntos
                                    </Button>
                                </GrantContent>
                            )}
                            <span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>
                        </div>
                    )}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
                    <div className="flex min-w-0 flex-col gap-[18px]">
                        {!model.isEditing && (
                        <>
                        <label
                            className={`relative flex cursor-pointer flex-wrap items-center gap-3 overflow-hidden rounded-2xl px-3.5 py-3 ${state.pdf === 'reading' ? 'cp-magic' : ''}`}
                            aria-busy={state.pdf === 'reading'}
                            style={state.pdf === 'done'
                                ? { border: '1px solid rgb(var(--k-ok-line))', background: 'rgb(var(--k-ok-sunken))' }
                                : state.pdf === 'reading'
                                    ? undefined
                                    : { background: `linear-gradient(135deg, ${ACCENT}, #8B3FC9)` }}
                        >
                            <span
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px]"
                                style={state.pdf === 'done'
                                    ? { background: 'rgb(var(--k-ok-soft))', color: 'rgb(var(--k-ok))' }
                                    : { background: 'rgba(255,255,255,0.18)', color: '#fff' }}
                            >
                                {state.pdf === 'reading' && <span className="cp-spin" aria-hidden />}
                                {state.pdf === 'done' && <CheckIcon />}
                                {state.pdf === 'idle' && <CloudUploadOutlinedIcon style={{ fontSize: 28 }} />}
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col gap-1">
                                <span className="text-[15px] font-extrabold" style={{ color: state.pdf === 'done' ? 'rgb(var(--k-ink))' : '#fff' }}>
                                    {state.pdf === 'reading' ? 'Leyendo la carátula…' : state.pdf === 'done' ? `Datos tomados de ${state.pdfName || 'la carátula'}` : 'Empieza subiendo la carátula en PDF'}
                                </span>
                                <span className="text-[13px]" style={{ color: state.pdf === 'done' ? 'rgb(var(--k-muted))' : 'rgba(255,255,255,0.85)' }}>
                                    {state.pdf === 'reading'
                                        ? 'Extraemos contratante, vigencia, primas y forma de pago.'
                                        : state.pdf === 'done'
                                            ? (model.revCount
                                                ? `Revisa los ${model.revCount} campos marcados; el resto se ve bien.`
                                                : 'Todo listo para revisar.')
                                            : 'Llenamos los datos por ti en segundos. Solo revisas y guardas.'}
                                </span>
                            </span>
                            <span
                                className="inline-flex h-[38px] shrink-0 items-center rounded-[11px] px-3.5 text-[13px] font-extrabold"
                                style={state.pdf === 'done'
                                    ? { border: '1px solid rgb(var(--k-line-strong))', background: 'rgb(var(--k-surface))', color: 'rgb(var(--k-ink))' }
                                    : { background: '#fff', color: '#1B1D29' }}
                            >
                                {state.pdf === 'done' ? 'Cambiar archivo' : state.pdf === 'reading' ? 'Leyendo…' : 'Subir carátula'}
                            </span>
                            <input
                                type="file"
                                accept="application/pdf"
                                className="absolute h-px w-px opacity-0"
                                disabled={state.pdf === 'reading'}
                                onChange={(event) => {
                                    model.uploadPdf(event.target.files?.[0])
                                    event.target.value = ''
                                }}
                            />
                        </label>
                        {state.pdf === 'idle' && <p className="-mt-2 m-0 text-center text-[13px] text-k-muted">o captura los datos a mano</p>}
                        </>
                        )}

                        <FormStepper labels={steps.labels} step={state.step} maxStep={state.maxStep} isOk={model.okSec} onStep={model.goToStep} />

                        {state.step === 0 && (
                            <section className="k-card">
                                <h2 className="k-card-title">Datos generales</h2>
                                <p className="m-0 -mt-2 text-sm text-k-muted">{(state.tried || state.triedSec[0]) && !model.okSec(0) ? `Faltan ${model.errorCount(0)} datos` : (model.okSec(0) && model.subs[0] ? model.subs[0] : steps.hints[0])}</p>
                                <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Ramo"
                                        value={state.ramo}
                                        disabled={model.isEditing || model.isRenewing}
                                        error={!!se.ramo}
                                        helperText={model.isRenewing ? 'La renovación conserva el ramo de la póliza original.' : fieldHelp(se, state.rev, 'ramo')}
                                        onChange={(e) => change.ramo(e.target.value)}
                                        slotProps={{
                                            select: {
                                                displayEmpty: true,
                                                ...selectMenu,
                                                renderValue: (value) => {
                                                    if (!value) return <em className="text-k-muted">Selecciona el ramo</em>
                                                    const meta = RAMO_META[value as RamoKey]
                                                    return (
                                                        <span className="inline-flex items-center gap-2.5">
                                                            <RamoMark ramo={String(value)} size={18} />
                                                            <span>{meta?.label || String(value)}</span>
                                                        </span>
                                                    )
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value=""><em>Selecciona el ramo</em></MenuItem>
                                        {RAMO_OPTIONS.map(([id, meta]) => (
                                            <MenuItem key={id} value={id}>
                                                <span className="inline-flex items-center gap-2.5 py-0.5">
                                                    <RamoMark ramo={id} size={18} />
                                                    <span>{meta.label}</span>
                                                </span>
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <div className="flex items-start gap-2 md:col-span-2">
                                        <Autocomplete
                                            className="min-w-0 flex-1"
                                            options={model.clientOptions}
                                            loading={model.clientSearching}
                                            value={g.contratante}
                                            filterOptions={(items) => items}
                                            noOptionsText="Escribe al menos 3 caracteres"
                                            loadingText="Buscando..."
                                            getOptionLabel={(option) => personName(option) || ''}
                                            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                            onChange={(_, value) => change.contratante(value)}
                                            onInputChange={(_, value, reason) => { if (reason === 'input') model.searchClientes(value) }}
                                            slotProps={{ paper: { className: 'v2-select-menu' } }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    className={`v2-field ${model.contratanteIncompleto && !se.contratante ? 'v2-review' : ''}`}
                                                    variant="filled"
                                                    fullWidth
                                                    required={!model.contratanteIncompleto}
                                                    label={model.contratanteIncompleto
                                                        ? <>Contratante<span className="MuiInputLabel-asterisk">*</span><span className="cl-tag">Completar</span></>
                                                        : 'Contratante'}
                                                    error={!!se.contratante}
                                                    helperText={model.contratanteIncompleto && !se.contratante
                                                        ? 'Faltan datos del cliente; complétalos con el botón de la derecha.'
                                                        : fieldHelp(se, state.rev, 'contratante', 'Busca por nombre (mín. 3 letras).')}
                                                />
                                            )}
                                        />
                                        <Badge
                                            color="error"
                                            variant="dot"
                                            overlap="rectangular"
                                            invisible={!model.contratanteIncompleto}
                                            sx={{
                                                marginTop: 'calc(15px * 1.25 + 8px)',
                                                flex: 'none',
                                                '& .MuiBadge-dot': {
                                                    width: 14,
                                                    height: 14,
                                                    minWidth: 14,
                                                    borderRadius: '50%',
                                                    border: '2px solid rgb(var(--k-surface))',
                                                },
                                            }}
                                        >
                                            <Button
                                                type="button"
                                                variant="contained"
                                                aria-label={g.contratante ? 'Editar cliente' : 'Nuevo cliente'}
                                                title={model.contratanteIncompleto ? 'Cliente con datos incompletos: edítalo' : g.contratante ? 'Editar cliente' : 'Nuevo cliente'}
                                                onClick={model.openClientDialog}
                                                className={model.contratanteIncompleto ? 'cp-magic-warn' : undefined}
                                                sx={{ ...newClientBtnSx, marginTop: 0 }}
                                            >
                                                {g.contratante ? <EditOutlinedIcon style={{ fontSize: 20 }} /> : <PersonAddAlt1Icon style={{ fontSize: 20 }} />}
                                            </Button>
                                        </Badge>
                                    </div>

                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Aseguradora"
                                        value={g.aseguradora}
                                        error={!!se.aseguradora}
                                        helperText={fieldHelp(se, state.rev, 'aseguradora')}
                                        onChange={(e) => change.aseguradora(e.target.value)}
                                        slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                    >
                                        <MenuItem value=""><em>Selecciona</em></MenuItem>
                                        {catalogs.aseguradoras.map((row) => (
                                            <MenuItem key={row.aseguradora.id} value={row.aseguradora.id ?? ''}>{aseguradoraLabel(row)}</MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Clave de agente"
                                        value={model.effClave}
                                        error={!!se.clave}
                                        helperText={model.claveAuto ? 'Asignada automáticamente' : fieldHelp(se, state.rev, 'clave', g.aseguradora ? undefined : 'Primero elige la aseguradora')}
                                        onChange={(e) => change.agente(e.target.value)}
                                        slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                    >
                                        <MenuItem value=""><em>{g.aseguradora ? 'Selecciona' : 'Primero elige la aseguradora'}</em></MenuItem>
                                        {model.claves.map((clave) => <MenuItem key={clave.id} value={clave.id ?? ''}>{claveLabel(clave)}</MenuItem>)}
                                    </TextField>

                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="No. de póliza"
                                        value={g.numero}
                                        error={!!se.numero}
                                        helperText={fieldHelp(se, state.rev, 'numero')}
                                        onChange={(e) => change.numero(e.target.value)}
                                        slotProps={{ htmlInput: { style: { textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums' } } }}
                                    />

                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="Plan / paquete"
                                        value={g.plan}
                                        placeholder={ramoMeta?.planPh || 'Ej. Plan contratado'}
                                        error={!!se.plan}
                                        helperText={fieldHelp(se, state.rev, 'plan')}
                                        onChange={(e) => change.plan(e.target.value)}
                                    />

                                    <TextField
                                        className="v2-field md:col-span-2" variant="filled" fullWidth required
                                        label="Concepto"
                                        value={g.concepto}
                                        placeholder={ramoMeta?.conceptoPh || 'Ej. Descripción de la póliza'}
                                        error={!!se.concepto}
                                        helperText={fieldHelp(se, state.rev, 'concepto')}
                                        onChange={(e) => change.concepto(e.target.value)}
                                    />

                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="Inicio de vigencia"
                                        type="date"
                                        value={g.desde}
                                        error={!!se.desde}
                                        helperText={fieldHelp(se, state.rev, 'desde')}
                                        onChange={(e) => change.desde(e.target.value)}
                                    />

                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="Fin de vigencia"
                                        type="date"
                                        value={model.hasta}
                                        error={!!se.hasta}
                                        helperText={model.finAuto ? 'Calculada: 1 año después del inicio' : fieldHelp(se, state.rev, 'hasta')}
                                        onChange={(e) => change.hasta(e.target.value)}
                                    />

                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Forma de pago"
                                        value={g.formaPago}
                                        disabled={model.isEditing}
                                        error={!!se.forma}
                                        helperText={model.isEditing ? 'No se cambia al editar: definiría otro número de recibos.' : fieldHelp(se, state.rev, 'forma')}
                                        onChange={(e) => change.formaPago(e.target.value)}
                                        slotProps={{ select: selectMenu }}
                                    >
                                        {FORMA_PAGO.map((item) => <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>)}
                                    </TextField>

                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Moneda"
                                        value={g.moneda}
                                        onChange={(e) => change.moneda(e.target.value)}
                                        slotProps={{ select: selectMenu }}
                                    >
                                        {catalogs.currency.map((m) => <MenuItem key={m.id} value={m.id ?? ''}>{monedaLabel(m)}</MenuItem>)}
                                    </TextField>

                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select
                                        label="Vendedor"
                                        value={g.vendedor}
                                        onChange={(e) => change.vendedor(e.target.value)}
                                        slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                    >
                                        <MenuItem value=""><em>Sin vendedor</em></MenuItem>
                                        {catalogs.vendedores.map((v) => <MenuItem key={v.id} value={v.id}>{personName(v)}</MenuItem>)}
                                    </TextField>

                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select
                                        label="Método de pago"
                                        value={g.conducto}
                                        onChange={(e) => change.conducto(e.target.value)}
                                        slotProps={{ select: selectMenu }}
                                    >
                                        {model.conductoOptions.map((item) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                                    </TextField>
                                </div>
                            </section>
                        )}

                        {state.step === 1 && (
                            <section className="k-card">
                                <h2 className="k-card-title">{steps.titles[1]}</h2>
                                <p className="m-0 -mt-2 text-sm text-k-muted">
                                    {(state.tried || state.triedSec[1]) && !model.okSec(1)
                                        ? `Faltan ${model.errorCount(1)} datos`
                                        : (model.okSec(1) && model.subs[1] ? model.subs[1] : steps.hints[1])}
                                </p>
                                {(state.ramo === 'vida' || state.ramo === 'salud') && <AseguradosStep model={model} />}
                                {state.ramo === 'autos' && <VehiculoStep model={model} />}
                                {state.ramo === 'danos' && <BienAseguradoStep model={model} />}
                            </section>
                        )}

                        {state.step === 2 && (
                            <>
                            <section className="k-card">
                                <h2 className="k-card-title">Primas</h2>
                                <p className="m-0 -mt-2 text-sm text-k-muted">{model.okSec(2) && model.subs[2] ? model.subs[2] : steps.hints[2]}</p>
                                <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="Prima neta"
                                        value={g.neta}
                                        placeholder="0.00"
                                        error={!!se.neta}
                                        helperText={fieldHelp(se, state.rev, 'neta')}
                                        onChange={(e) => change.neta(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        onBlur={() => change.amountBlur('neta')}
                                        slotProps={{ input: amountInput, htmlInput: amountHtmlInput }}
                                    />
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth
                                        label="Derecho de póliza"
                                        value={g.derecho}
                                        placeholder="0.00"
                                        onChange={(e) => change.derecho(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        onBlur={() => change.amountBlur('derecho')}
                                        slotProps={{ input: amountInput, htmlInput: amountHtmlInput }}
                                    />
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth
                                        label="Financiamiento"
                                        value={g.financiamiento}
                                        placeholder="0.00"
                                        onChange={(e) => change.financiamiento(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        onBlur={() => change.amountBlur('financiamiento')}
                                        slotProps={{ input: amountInput, htmlInput: amountHtmlInput }}
                                    />
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth
                                        label="IVA"
                                        value={model.ivaValue}
                                        placeholder="0.00"
                                        helperText={model.ivaHint}
                                        onChange={(e) => change.iva(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        onBlur={() => change.amountBlur('iva')}
                                        slotProps={{ input: amountInput, htmlInput: amountHtmlInput }}
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className="text-[13px] font-bold text-k-ink">Prorratear entre todos los recibos</span>
                                    <div className="flex flex-wrap gap-4">
                                        <FormControlLabel control={<Switch checked={state.prorrDer} onChange={change.prorrDer} />} label="Derecho" />
                                        <FormControlLabel control={<Switch checked={state.prorrFin} onChange={change.prorrFin} />} label="Financiamiento" />
                                    </div>
                                    <span className="text-xs text-k-muted">Si no se prorratea, el monto se cobra completo en el primer recibo.</span>
                                </div>
                                <div className="flex flex-col gap-2 rounded-[14px] border border-k-line bg-k-bg px-4 py-3.5 text-sm">
                                    <div className="flex justify-between text-k-ink-2"><span>Prima neta</span><span className="tabular-nums">${formatAmount(model.neta)}</span></div>
                                    <div className="flex justify-between text-k-ink-2"><span>Derecho</span><span className="tabular-nums">${formatAmount(model.derecho)}</span></div>
                                    <div className="flex justify-between text-k-ink-2"><span>Financiamiento</span><span className="tabular-nums">${formatAmount(model.financiamiento)}</span></div>
                                    <div className="flex justify-between text-k-ink-2"><span>IVA</span><span className="tabular-nums">${formatAmount(model.iva)}</span></div>
                                    <div className="flex justify-between border-t border-k-line pt-2 font-extrabold text-k-ink"><span>Prima total</span><span className="tabular-nums">${formatAmount(model.total)}</span></div>
                                </div>
                            </section>

                            <section className="k-card">
                                <h2 className="k-card-title">Comisión</h2>
                                <p className="m-0 -mt-2 text-sm text-k-muted">Sobre la prima neta. Captura el porcentaje o el monto; el otro se calcula.</p>
                                <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="Porcentaje"
                                        value={model.pctValue}
                                        placeholder="0.00"
                                        error={!!se.pct}
                                        helperText={model.pctAuto ? `Sugerido para ${ramoMeta?.label || 'el ramo'} según la clave de agente` : fieldHelp(se, {}, 'pct')}
                                        onChange={(e) => change.pct(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        onBlur={change.pctBlur}
                                        slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> }, htmlInput: amountHtmlInput }}
                                    />
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="Monto"
                                        value={model.comisionValue}
                                        placeholder="0.00"
                                        error={!!se.pct}
                                        helperText={model.neta > 0 ? undefined : 'Captura primero la prima neta'}
                                        disabled={!(model.neta > 0)}
                                        onChange={(e) => change.comisionMonto(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        onBlur={() => change.amountBlur('comisionMonto')}
                                        slotProps={{ input: amountInput, htmlInput: amountHtmlInput }}
                                    />
                                </div>
                            </section>
                            </>
                        )}

                        {state.step === LAST_STEP && (
                            <section className="k-card">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h2 className="k-card-title">Recibos</h2>
                                        <p className="m-0 text-sm text-k-muted">
                                            {model.isEditing
                                                ? 'Recibos de la póliza. Ajusta montos, fechas o el pago de cada uno.'
                                                : 'Se generan con la forma de pago y las primas. Puedes editar montos, fechas y pagos, o recalcular.'}
                                        </p>
                                    </div>
                                    {!model.isEditing && (
                                        <Button type="button" variant="outlined" sx={outlineBtnSx} onClick={() => model.calcularRecibos()}>Recalcular</Button>
                                    )}
                                </div>
                                {model.recDiff && (
                                    <div role="status" className="flex gap-2.5 rounded-xl px-3.5 py-3 text-[13px] font-semibold leading-snug" style={{ background: '#FFF0DB', color: '#7A4A06' }}>
                                        <WarningAmberRoundedIcon style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                                        <span>Los recibos suman ${formatAmount(model.sumRec)} y la prima total es ${formatAmount(model.total)} (diferencia de ${formatAmount(Math.abs(model.sumRec - model.total))}).</span>
                                    </div>
                                )}
                                <div className="overflow-x-auto">
                                    <table className="cl-table cl-rec min-w-[720px]">
                                        <thead>
                                            <tr>
                                                <th aria-label="Detalle" />
                                                <th>#</th>
                                                <th>Vigencia desde</th>
                                                <th style={amountCol}>Prima neta</th>
                                                <th style={amountCol}>Derecho</th>
                                                <th style={amountCol}>Financiamiento</th>
                                                <th style={amountCol}>IVA</th>
                                                <th style={amountCol}>Total</th>
                                                <th>Pagado</th>
                                                <th aria-label="Acciones" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {state.recibos.map((r, i) => {
                                                const open = model.expandedRecibo === i
                                                const pagado = r.status === 'pagado'
                                                return (
                                                    <React.Fragment key={r.id || r.serie || i}>
                                                        <tr>
                                                            <td>
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label={open ? `Ocultar detalle del recibo ${i + 1}` : `Ver detalle del recibo ${i + 1}`}
                                                                    aria-expanded={open}
                                                                    onClick={() => model.toggleRecibo(i)}
                                                                    sx={{ color: 'rgb(var(--k-muted))' }}
                                                                >
                                                                    <ExpandMoreIcon style={{ fontSize: 18, transform: open ? 'rotate(180deg)' : undefined, transition: 'transform .15s' }} />
                                                                </IconButton>
                                                            </td>
                                                            <td className="font-bold tabular-nums">
                                                                {r.serie || i + 1}
                                                                {r.edited && <span className="block text-[10px] font-extrabold uppercase tracking-wide text-k-accent-ink">Editado</span>}
                                                            </td>
                                                            <td className="whitespace-nowrap text-xs text-k-muted">{fmtShort(r.fecInicial)}</td>
                                                            {RECIBO_FIELDS.map((field) => (
                                                                <td key={field}>
                                                                    <TextField
                                                                        size="small"
                                                                        variant="standard"
                                                                        value={model.reciboValue(i, field)}
                                                                        onChange={(e) => model.changeRecibo(i, field, e.target.value)}
                                                                        onFocus={(e) => e.target.select()}
                                                                        onBlur={model.blurRecibo}
                                                                        slotProps={{
                                                                            htmlInput: {
                                                                                inputMode: 'decimal',
                                                                                'aria-label': `${field} del recibo ${i + 1}`,
                                                                            },
                                                                        }}
                                                                        sx={reciboInputSx}
                                                                    />
                                                                </td>
                                                            ))}
                                                            <td className="whitespace-nowrap text-right font-bold tabular-nums">${formatAmount(r.primaTotal)}</td>
                                                            <td>
                                                                <Switch size="small" checked={pagado} onChange={() => model.togglePagado(i)} slotProps={{ input: { 'aria-label': `Recibo ${i + 1} pagado` } }} />
                                                            </td>
                                                            <td>
                                                                {i > 0 && (
                                                                    <Tooltip title="Aplicar estos montos a todos los recibos subsecuentes" arrow>
                                                                        <IconButton size="small" aria-label={`Aplicar montos del recibo ${i + 1} a los subsecuentes`} onClick={() => model.askApplyToAll(i)} sx={{ color: 'rgb(var(--k-accent-ink))' }}>
                                                                            <ContentCopyOutlinedIcon style={{ fontSize: 16 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </td>
                                                        </tr>
                                                        {open && (
                                                            <tr>
                                                                <td colSpan={10} className="bg-k-bg">
                                                                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-4 px-2 py-3 md:grid-cols-3">
                                                                        <TextField className="v2-field" variant="filled" fullWidth type="date" label="Vigencia inicial" value={r.fecInicial || ''} onChange={(e) => model.changeReciboDate(i, 'fecInicial', e.target.value)} />
                                                                        <TextField className="v2-field" variant="filled" fullWidth type="date" label="Vigencia final" value={r.fecFinal || ''} onChange={(e) => model.changeReciboDate(i, 'fecFinal', e.target.value)} />
                                                                        <TextField className="v2-field" variant="filled" fullWidth type="date" label="Vencimiento" value={r.vencimiento || ''} onChange={(e) => model.changeReciboDate(i, 'vencimiento', e.target.value)} />
                                                                        {pagado && (
                                                                            <>
                                                                                <TextField className="v2-field" variant="filled" fullWidth type="date" label="Fecha de pago" value={r.fecPago || ''} onChange={(e) => model.changeReciboDate(i, 'fecPago', e.target.value)} />
                                                                                <TextField className="v2-field md:col-span-2" variant="filled" fullWidth label="Folio de pago" value={r.folioPago} helperText="Opcional · máximo 50 caracteres" onChange={(e) => model.changeFolioPago(i, e.target.value)} />
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                )
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan={7} className="font-extrabold" style={totalRowBorder}>Suma de recibos</td>
                                                <td className="whitespace-nowrap text-right font-extrabold tabular-nums" style={totalRowBorder}>${formatAmount(model.sumRec)}</td>
                                                <td colSpan={2} style={totalRowBorder} />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </section>
                        )}

                        <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                            {state.step > 0 && (
                                <Button type="button" variant="outlined" sx={outlineBtnSx} onClick={model.stepBack}>Atrás</Button>
                            )}
                            <Button type="button" variant="contained" sx={primaryBtnSx} onClick={model.stepNext} startIcon={state.step === LAST_STEP ? <CheckMarkIcon /> : undefined}>
                                {state.step === LAST_STEP ? 'Guardar póliza' : 'Continuar'}
                            </Button>
                        </div>
                    </div>

                    <aside className="cl-aside flex w-full min-w-0 flex-col gap-3 overflow-hidden rounded-[20px] border border-k-line bg-k-surface p-4" aria-label="Resumen de la póliza">
                        <div className="flex min-w-0 items-center gap-3">
                            <RamoMark ramo={ramoMeta?.label || state.ramo || 'póliza'} boxed />
                            <span className="flex min-w-0 flex-1 flex-col overflow-hidden">
                                <span className="truncate text-xs font-bold text-k-muted">
                                    {ramoMeta?.label || 'Póliza'}
                                    {g.aseguradora ? ` · ${aseguradoraLabel(model.aseguradoraRow)}` : ''}
                                </span>
                                <Tooltip title={model.resumenNumero} arrow placement="top">
                                    <span className="block min-w-0 truncate text-[15px] font-extrabold tabular-nums text-k-ink">{model.resumenNumero}</span>
                                </Tooltip>
                            </span>
                        </div>
                        <div className="flex flex-col gap-0.5 rounded-[14px] bg-k-accent-soft px-4 py-3.5">
                            <span className="text-xs font-bold text-k-accent-ink">Prima total</span>
                            <span className="text-[28px] font-extrabold tracking-[-0.02em] tabular-nums text-k-ink">${formatAmount(model.total)}</span>
                            <span className="text-[13px] text-k-ink-2">{model.forma.parcialidades} recibo(s) de ${formatAmount(model.forma.parcialidades ? model.total / model.forma.parcialidades : 0)} · {model.forma.label}</span>
                        </div>
                        <div className="flex justify-between text-[13px] text-k-muted">
                            <span>Tu comisión</span>
                            <strong className="tabular-nums text-k-ink">${formatAmount(model.comision)} ({formatPct(model.pctN)}%)</strong>
                        </div>
                        <div className="flex flex-col gap-1 border-t border-k-line pt-3">
                            {steps.titles.map((label, i) => {
                                const ok = model.okSec(i)
                                const bad = state.tried && !ok
                                return (
                                    <button
                                        key={label}
                                        type="button"
                                        onClick={() => model.goToStep(i)}
                                        className="flex min-h-[38px] items-center gap-2.5 rounded-[10px] border-0 bg-transparent px-1.5 text-left text-sm font-semibold text-k-ink hover:bg-k-hover"
                                    >
                                        <span
                                            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                                            style={ok ? { background: 'rgb(var(--k-ok))' } : bad ? { border: '2px solid rgb(var(--k-danger))' } : { border: '2px solid rgb(var(--k-line-strong))' }}
                                        >
                                            {ok && <CheckIcon style={{ fontSize: 11, color: '#fff' }} />}
                                        </span>
                                        <span className="flex-1">{label}</span>
                                        {bad && <span className="text-xs font-extrabold text-k-danger">{model.errorCount(i)}</span>}
                                    </button>
                                )
                            })}
                        </div>
                        {model.errList.length > 0 && (
                            <div role="alert" className="rounded-xl bg-k-danger-soft px-3 py-2.5 text-[13px] font-semibold leading-snug text-k-danger-ink">
                                Falta: {model.errList.join(', ')}.
                            </div>
                        )}
                        <Tooltip title={model.canSave ? '' : 'Llega al paso de Recibos para guardar'} arrow placement="top">
                            <span className="block w-full">
                                <Button type="button" variant="contained" fullWidth disabled={!model.canSave} sx={{ ...primaryBtnSx, height: 52, width: '100%', '&.Mui-disabled': { opacity: 0.45, color: '#fff', backgroundColor: 'rgb(var(--k-primary))' } }} onClick={model.guardar} startIcon={<CheckMarkIcon />}>
                                    Guardar póliza
                                </Button>
                            </span>
                        </Tooltip>
                        {!model.isEditing && (
                            <Button type="button" variant="text" sx={{ ...outlineBtnSx, border: 0, color: 'rgb(var(--k-accent-ink))', height: 36 }} onClick={model.openResetDialog}>
                                Volver a comenzar
                            </Button>
                        )}
                    </aside>
                </div>
            </div>
        </KarteraAdminTemplate>
    )
}

export default PolicyForm
